/**
 * §7.23 Operator Workspace — Phase 6 CD-spec implementation.
 *
 * One surface that ties all six operator-workflow R1-R6 deliverables into
 * a single loop, exactly as the CD spec sets it up: these aren't six
 * features, they're one workflow.
 *
 *   [PersonaLens]                                       [EDU chip]
 *   ┌─ Tabs (Inbox / Alerts / Compare / Annotations) ─┐  ┌─ Pins rail ─┐
 *   │                                                  │  │ watchlists   │
 *   │  <active surface>                                │  │ + session    │
 *   │                                                  │  │ pins         │
 *   │                                                  │  │              │
 *   └──────────────────────────────────────────────────┘  │ [Brief →]   │
 *                                                          └──────────────┘
 *
 * The pin verb on the Inbox routes to addPin() → appended to the first
 * watchlist for this session (production would persist server-side). The
 * "Build briefing →" button on the pins rail opens the R6 modal as a
 * centred overlay (per CD verifier's modal-positioning rail).
 *
 * Sample data lives in src/workflow/sample-data.ts; clearly marked
 * illustrative everywhere it surfaces. Real workflow state lives in the
 * Phase 7.x audit-trail + data-retention build.
 */

import { useEffect, useState } from 'react';
import { Inbox } from '../workflow/Inbox';
import { AlertRules } from '../workflow/AlertRules';
import { Comparison } from '../workflow/Comparison';
import { Annotations } from '../workflow/Annotations';
import { CurateQueue, pendingReviewCount } from '../workflow/CurateQueue';
import { PinsRail } from '../workflow/PinsRail';
import { Briefing } from '../workflow/Briefing';
import { AuditPanel } from '../audit/AuditPanel';
import { auditCapture } from '../audit/capture';
import {
  SAMPLE_INBOX, SAMPLE_LAST_VISIT_HOURS, SAMPLE_RULES,
  SAMPLE_WATCHLISTS, SAMPLE_ANNOTATIONS, SAMPLE_COMPARABLES,
} from '../workflow/sample-data';
import { PERSONA_META, type Persona, type InboxItem } from '../workflow/types';

type Tab = 'inbox' | 'alerts' | 'compare' | 'annos' | 'curate' | 'audit';

// Count of cultural-risk entries awaiting curator sign-off — drives the
// Curate tab badge. Computed once at module load (the pending file is bundled
// JSON, so the count is stable for the session).
const PENDING_CURATE = pendingReviewCount();

// `count` (optional) renders a badge after the label — used by the R7 Curate
// tab to surface how many politically-contested entries await sign-off.
const TABS: ReadonlyArray<{ k: Tab; label: string; sec: string; count?: number }> = [
  { k: 'inbox', label: 'Inbox', sec: 'R1' },
  { k: 'alerts', label: 'Alert rules', sec: 'R2' },
  { k: 'compare', label: 'Comparison', sec: 'R3' },
  { k: 'annos', label: 'Annotations', sec: 'R5' },
  { k: 'curate', label: 'Curate', sec: 'R7', count: PENDING_CURATE },
  { k: 'audit', label: 'Audit trail', sec: 'P7.1' },
];

const VALID_TABS: ReadonlySet<Tab> = new Set(TABS.map(t => t.k));

/** Deep-link entry — read `?tab=<name>` off the URL hash query so the
 *  Cultural Risk sidebar's "N pending curator review" pointer can land
 *  directly on the Curate tab. Same pattern as §7.22 Feed Health's
 *  sovereignty deep-link. Unrecognised / absent → default 'inbox'. */
function readTabFromHash(): Tab | null {
  if (typeof window === 'undefined') return null;
  const q = window.location.hash.split('?')[1] ?? '';
  const raw = new URLSearchParams(q).get('tab');
  return raw && VALID_TABS.has(raw as Tab) ? (raw as Tab) : null;
}

export function OperatorWorkspace() {
  const [tab, setTab] = useState<Tab>(() => readTabFromHash() ?? 'inbox');
  const [persona, setPersona] = useState<Persona>('who');
  const [sessionPins, setSessionPins] = useState<InboxItem[]>([]);
  const [briefOpen, setBriefOpen] = useState(false);

  // Phase 7.1 audit-trail wiring — operator actions emit append-only events
  // so they show up in the §P7.1 Audit trail tab. NEVER logs the user-
  // generated content (annotation text, briefing payload) per pii-redaction.md.
  useEffect(() => {
    auditCapture.emit({ event: { type: 'session.start', subjectIds: [], payload: { surface: '§7.23' } } });
    return () => {
      auditCapture.emit({ event: { type: 'session.end', subjectIds: [], payload: {} } });
    };
  }, []);

  // Re-sync the active tab if the hash query changes while mounted (operator
  // clicks the sidebar's Curate deep-link while Workspace is already open).
  useEffect(() => {
    const onHash = () => { const next = readTabFromHash(); if (next) setTab(next); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const addPin = (item: InboxItem) => {
    setSessionPins(prev => {
      if (prev.find(p => p.id === item.id)) return prev;
      auditCapture.emit({ event: {
        type: 'pin.add',
        surface: '§7.23',
        subjectIds: [item.id],
        payload: { tier: item.tier, pathogen: item.pathogen },
      } });
      return [item, ...prev];
    });
  };

  const switchPersona = (next: Persona) => {
    if (next === persona) return;
    auditCapture.emit({ event: {
      type: 'persona.switch', surface: '§7.23', subjectIds: [], payload: { from: persona, to: next },
    } });
    setPersona(next);
  };

  const switchTab = (next: Tab) => {
    if (next === tab) return;
    auditCapture.emit({ event: {
      type: 'surface.view', surface: '§7.23', subjectIds: [], payload: { from: tab, to: next },
    } });
    setTab(next);
  };

  const openBriefing = () => {
    auditCapture.emit({ event: {
      type: 'briefing.export', surface: '§7.23',
      subjectIds: sessionPins.map(p => p.id),
      payload: { format: 'preview' },
    } });
    setBriefOpen(true);
  };

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.23</span>
        <h1 className="surface__title">Operator Workspace</h1>
      </div>
      <div className="surface__sub">
        The operator loop: Inbox → Pin → Alerts → Annotate → Compare → Brief.
        Six surfaces tied into one workflow with a persona lens + persistent
        pins rail. ILLUSTRATIVE sample data — production state lives in the
        Phase 7.x audit-trail + data-retention build.
      </div>

      <PersonaLens persona={persona} setPersona={switchPersona} />

      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 16, alignItems: 'start',
        marginTop: 16,
      }}>
        <div style={{
          background: 'var(--bm-bg)',
          border: '1px solid var(--bm-border)',
          borderRadius: 'var(--bm-radius-md)',
          overflow: 'hidden',
        }}>
          <nav style={{
            display: 'flex', gap: 0,
            borderBottom: '1px solid var(--bm-border)',
            background: 'var(--bm-bg-subtle)',
          }}>
            {TABS.map(t => (
              <button
                key={t.k}
                type="button"
                onClick={() => switchTab(t.k)}
                style={{
                  padding: '10px 14px',
                  background: tab === t.k ? 'var(--bm-bg)' : 'transparent',
                  border: 'none',
                  borderBottom: `2px solid ${tab === t.k ? 'var(--bm-accent, #6ea3ff)' : 'transparent'}`,
                  fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
                  color: tab === t.k ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'baseline', gap: 6,
                }}
              >
                {t.label}
                {t.count !== undefined && t.count > 0 && (
                  <span
                    aria-label={`${t.count} pending review`}
                    style={{
                      fontSize: 9.5, fontWeight: 700, lineHeight: 1,
                      padding: '2px 5px', borderRadius: 999,
                      background: 'var(--bm-threat-4, #c1121f)', color: 'var(--bm-bg, #fff)',
                    }}
                  >{t.count}</span>
                )}
                <span style={{ fontSize: 9.5, color: 'var(--bm-text-subtle)' }}>{t.sec}</span>
              </button>
            ))}
          </nav>

          <div style={{ padding: 16 }}>
            {tab === 'inbox' && (
              <Inbox
                persona={persona}
                items={SAMPLE_INBOX}
                lastVisitHours={SAMPLE_LAST_VISIT_HOURS}
                onPin={addPin}
              />
            )}
            {tab === 'alerts' && <AlertRules rules={SAMPLE_RULES} />}
            {tab === 'compare' && <Comparison comparables={SAMPLE_COMPARABLES} />}
            {tab === 'annos' && <Annotations annotations={SAMPLE_ANNOTATIONS} />}
            {tab === 'curate' && <CurateQueue />}
            {tab === 'audit' && <AuditPanel />}
          </div>
        </div>

        <PinsRail
          watchlists={SAMPLE_WATCHLISTS}
          sessionPins={sessionPins}
          onOpenBriefing={openBriefing}
        />
      </div>

      {briefOpen && (
        <BriefingModal onClose={() => setBriefOpen(false)} />
      )}
    </div>
  );
}

function PersonaLens({ persona, setPersona }: { persona: Persona; setPersona: (p: Persona) => void }) {
  const personas: Persona[] = ['who', 'cmo', 'usg'];
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      marginTop: 12, padding: '6px 10px',
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-sm)',
    }}>
      <span style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700, letterSpacing: '.4px',
        textTransform: 'uppercase', color: 'var(--bm-text-muted)',
      }}>Lens</span>
      <div style={{ display: 'inline-flex', border: '1px solid var(--bm-border)', borderRadius: 5 }}>
        {personas.map(p => (
          <button
            key={p}
            type="button"
            onClick={() => setPersona(p)}
            title={PERSONA_META[p].name}
            style={{
              padding: '4px 10px',
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
              background: persona === p ? 'var(--bm-bg)' : 'transparent',
              color: persona === p ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
              border: 'none',
              cursor: 'pointer',
            }}
          >{PERSONA_META[p].short}</button>
        ))}
      </div>
    </div>
  );
}

/** Phase 8.4 — Slide-out drawer for the Briefing (CD R9 Q6 verdict:
 *  "operators want to keep referencing the map while assembling the
 *  brief; drawer beats modal"). Anchored to the right edge, max-width
 *  capped so the underlying workspace stays partially visible. Click
 *  outside or press Esc to close. */
function BriefingModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', justifyContent: 'flex-end',
        background: 'rgba(0, 0, 0, 0.45)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        /* `pacc-light` — the Briefing export is a leadership OUTPUT, so it
           renders as a light, print-friendly island even though it floats
           over the dark command centre. The class re-declares --bm-* to the
           light brand for this subtree (so the inline var(--bm-bg) below now
           resolves to white) and re-tunes threat/prov to stay AA on white. */
        className="pacc-light pacc-light--drawer"
        style={{
          width: 'min(640px, 92vw)', height: '100%',
          overflowY: 'auto' as const,
          background: 'var(--bm-bg, #fff)',
          boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.25)',
          borderLeft: '1px solid var(--bm-border)',
          animation: 'bm-drawer-slide 0.18s ease-out',
        }}
      >
        <Briefing onClose={onClose} />
      </div>
    </div>
  );
}
